/**
 * UranIA PA — Pagina admin "Corpus & Ingestion" (``/admin/corpus``).
 *
 * Console tecnica della pipeline RAG: upload di un PDF/DOCX/TXT singolo e
 * monitoraggio del job Arq di indicizzazione (chunk + embed e5-large +
 * scrittura su Qdrant + OpenSearch). E' la vista pipeline; la lista
 * curata dei documenti (categoria, tag, fonte, anno) vive su
 * ``/admin/banca-dati``.
 *
 *   1. Form upload (file + doc_id + scope + data_classification).
 *   2. POST ``/api/v1/admin/corpus/ingest`` → ricezione ``job_id``.
 *   3. Polling ``GET /api/v1/admin/corpus/ingest/{job_id}`` ogni 3s
 *      finche' lo stato e' ``in_progress`` / ``queued`` / ``deferred``.
 *   4. Render del risultato finale (chunks_count, sezioni_count, ecc.).
 *
 * RBAC guard UX: solo admin (``platform_admin`` / ``tenant_admin``).
 * Autorizzazione reale server-side (vedi ``routes/corpus_ingest.py``).
 */

import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import type { FormEvent, JSX } from 'react';

import {
  CorpusApiError,
  enqueueCorpusIngest,
  getCorpusJobStatus,
} from '../../../lib/admin/corpus-api.ts';
import type {
  CorpusCategoria,
  CorpusDataClassification,
  CorpusIngestEnqueueDto,
  CorpusJobStatusDto,
  CorpusTenantScope,
} from '../../../lib/admin/corpus-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsAnyAdmin } from '../../../lib/auth/rbac.ts';
import { Route as RootRoute } from '../../__root.tsx';

const DOC_ID_PATTERN = /^[a-z0-9_\-\.]+$/u;
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

export function AdminCorpusPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isAdmin = principalIsAnyAdmin(principal);

  const fileId = useId();
  const docIdId = useId();
  const scopeId = useId();
  const classId = useId();
  const categoriaId = useId();

  const [file, setFile] = useState<File | null>(null);
  const [docId, setDocId] = useState('');
  const [categoria, setCategoria] = useState<CorpusCategoria>('regioni');
  const [tenantScope, setTenantScope] = useState<CorpusTenantScope>('core');
  const [dataClassification, setDataClassification] =
    useState<CorpusDataClassification>('internal');

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enqueue, setEnqueue] = useState<CorpusIngestEnqueueDto | null>(null);
  const [jobStatus, setJobStatus] = useState<CorpusJobStatusDto | null>(null);

  // Polling Arq job ogni 3s finche' non e' complete/not_found.
  useEffect(() => {
    if (enqueue === null) return undefined;
    let cancelled = false;
    let timer: number | undefined;
    const poll = async (): Promise<void> => {
      try {
        const next = await getCorpusJobStatus(enqueue.job_id);
        if (cancelled) return;
        setJobStatus(next);
        if (
          next.status === 'queued' ||
          next.status === 'deferred' ||
          next.status === 'in_progress'
        ) {
          timer = window.setTimeout(poll, 3000);
        }
      } catch {
        // best-effort
      }
    };
    void poll();
    return (): void => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [enqueue]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      setSubmitError(null);
      if (file === null) {
        setSubmitError(t('admin.corpus.errors.file_required'));
        return;
      }
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        setSubmitError(t('admin.corpus.errors.generic'));
        return;
      }
      if (!DOC_ID_PATTERN.test(docId)) {
        setSubmitError(t('admin.corpus.errors.invalid_doc_id'));
        return;
      }
      setIsSubmitting(true);
      try {
        const dto = await enqueueCorpusIngest({
          file,
          docId,
          titolo: docId,
          categoria,
          tenantScope,
          dataClassification,
        });
        setEnqueue(dto);
        setJobStatus(null);
      } catch (err) {
        if (err instanceof CorpusApiError) {
          setSubmitError(err.detail ?? err.message);
        } else {
          setSubmitError(t('admin.corpus.errors.generic'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [file, docId, categoria, tenantScope, dataClassification, t],
  );

  if (!isAuthenticated) {
    return (
      <section data-testid="admin-corpus-unauthenticated">
        <h1 className="h3 mb-3">{t('admin.corpus.title')}</h1>
        <div className="alert alert-info">
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link to="/login" className="btn btn-primary">
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }
  if (!isAdmin) {
    return (
      <section data-testid="admin-corpus-forbidden">
        <h1 className="h3 mb-3">{t('admin.corpus.title')}</h1>
        <div className="alert alert-warning">
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-corpus" data-testid="admin-corpus-page">
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          {t('admin.corpus.back_to_console')}
        </Link>
      </nav>

      <div
        className="rounded p-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <span
          className="badge mb-2"
          style={{
            backgroundColor: 'rgba(255,255,255,0.18)',
            color: '#fff',
            fontSize: '0.7rem',
            letterSpacing: '0.05em',
          }}
        >
          ADMIN · CORPUS &amp; INGESTION
        </span>
        <h1 className="h3 mb-2">{t('admin.corpus.title')}</h1>
        <p className="mb-0 opacity-90 small">{t('admin.corpus.subtitle')}</p>
      </div>

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-white border-bottom py-2 px-3">
              <span
                className="text-uppercase text-muted fw-semibold"
                style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
              >
                {t('admin.corpus.form_header')}
              </span>
            </div>
            <div className="card-body">
              <form onSubmit={onSubmit} data-testid="admin-corpus-form">
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1" htmlFor={fileId}>
                    {t('admin.corpus.fields.file')}
                  </label>
                  <input
                    id={fileId}
                    type="file"
                    className="form-control form-control-sm"
                    accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    onChange={(e): void => setFile(e.target.files?.[0] ?? null)}
                    required
                    data-testid="admin-corpus-form-file"
                  />
                  <small className="form-text text-muted">
                    {t('admin.corpus.fields.file_hint')}
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1" htmlFor={docIdId}>
                    {t('admin.corpus.fields.doc_id')}
                  </label>
                  <input
                    id={docIdId}
                    type="text"
                    className="form-control form-control-sm font-monospace"
                    value={docId}
                    onChange={(e): void => setDocId(e.target.value)}
                    placeholder="es. manuale-attuazione-v5.0-2026"
                    required
                    pattern={DOC_ID_PATTERN.source}
                    data-testid="admin-corpus-form-doc-id"
                  />
                  <small className="form-text text-muted">
                    {t('admin.corpus.fields.doc_id_hint')}
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1" htmlFor={categoriaId}>
                    Categoria
                  </label>
                  <select
                    id={categoriaId}
                    className="form-select form-select-sm"
                    value={categoria}
                    onChange={(e): void => setCategoria(e.target.value as CorpusCategoria)}
                    data-testid="admin-corpus-form-categoria"
                  >
                    <option value="eur_lex">EUR-Lex</option>
                    <option value="ministeri">Ministeri</option>
                    <option value="regioni">Regioni</option>
                    <option value="corpus_scientifico">Corpus scientifico</option>
                  </select>
                </div>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small text-muted mb-1" htmlFor={scopeId}>
                      {t('admin.corpus.fields.scope')}
                    </label>
                    <select
                      id={scopeId}
                      className="form-select form-select-sm"
                      value={tenantScope}
                      onChange={(e): void => setTenantScope(e.target.value as CorpusTenantScope)}
                    >
                      <option value="core">{t('admin.corpus.scope.core')}</option>
                      <option value="tenant">{t('admin.corpus.scope.tenant')}</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted mb-1" htmlFor={classId}>
                      {t('admin.corpus.fields.data_classification')}
                    </label>
                    <select
                      id={classId}
                      className="form-select form-select-sm"
                      value={dataClassification}
                      onChange={(e): void => setDataClassification(e.target.value as CorpusDataClassification)}
                    >
                      <option value="public">public</option>
                      <option value="internal">internal</option>
                      <option value="restricted">restricted</option>
                      <option value="secret">secret</option>
                    </select>
                  </div>
                </div>
                {submitError !== null && (
                  <div className="alert alert-danger small mt-3 mb-0" role="alert">
                    {submitError}
                  </div>
                )}
                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={isSubmitting}
                    data-testid="admin-corpus-form-submit"
                  >
                    {isSubmitting ? t('admin.corpus.submitting') : t('admin.corpus.submit')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {enqueue !== null && (
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom py-2 px-3">
                <span
                  className="text-uppercase text-muted fw-semibold"
                  style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
                >
                  {t('admin.corpus.job_header')}
                </span>
              </div>
              <div className="card-body">
                <p className="small mb-1">
                  <strong>{t('admin.corpus.job.id')}:</strong>{' '}
                  <code>{enqueue.job_id}</code>
                </p>
                <p className="small mb-2">
                  <strong>{t('admin.corpus.job.doc_id')}:</strong>{' '}
                  <code>{enqueue.doc_id}</code>
                </p>
                {jobStatus === null && (
                  <p className="small text-muted">
                    {t('admin.corpus.job.in_progress_hint')}
                  </p>
                )}
                {jobStatus !== null &&
                  (jobStatus.status === 'queued' ||
                    jobStatus.status === 'deferred' ||
                    jobStatus.status === 'in_progress') && (
                    <p className="small text-muted">
                      {t('admin.corpus.job.in_progress_hint')}
                    </p>
                  )}
                {jobStatus !== null && jobStatus.status === 'complete' && (
                  <div>
                    {jobStatus.error !== null ? (
                      <div className="alert alert-danger small mb-0" role="alert">
                        <strong>{t('admin.corpus.job.failed_title')}</strong>
                        <p className="mb-0">{jobStatus.error}</p>
                      </div>
                    ) : (
                      <div className="alert alert-success small mb-0" role="status">
                        <strong>{t('admin.corpus.job.success_title')}</strong>
                        {jobStatus.result !== null && (
                          <ul className="mb-0 ps-3 mt-1">
                            {typeof jobStatus.result.chunks_count === 'number' && (
                              <li>
                                {t('admin.corpus.job.chunks_count', {
                                  count: jobStatus.result.chunks_count,
                                })}
                              </li>
                            )}
                            {typeof jobStatus.result.chunks_persisted === 'number' && (
                              <li>
                                {t('admin.corpus.job.chunks_persisted', {
                                  count: jobStatus.result.chunks_persisted,
                                })}
                              </li>
                            )}
                            {typeof jobStatus.result.vectors_count === 'number' && (
                              <li>
                                {t('admin.corpus.job.vectors_count', {
                                  count: jobStatus.result.vectors_count,
                                })}
                              </li>
                            )}
                            {typeof jobStatus.result.sezioni_count === 'number' && (
                              <li>
                                {t('admin.corpus.job.sezioni_count', {
                                  count: jobStatus.result.sezioni_count,
                                })}
                              </li>
                            )}
                            {typeof jobStatus.result.pages_count === 'number' && (
                              <li>
                                {t('admin.corpus.job.pages_count', {
                                  count: jobStatus.result.pages_count,
                                })}
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-5">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom py-2 px-3">
              <span
                className="text-uppercase text-muted fw-semibold"
                style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
              >
                {t('admin.corpus.pipeline.title')}
              </span>
            </div>
            <div className="card-body">
              <ol className="small ps-3 mb-0">
                <li>{t('admin.corpus.pipeline.step_extract')}</li>
                <li>{t('admin.corpus.pipeline.step_chunk')}</li>
                <li>{t('admin.corpus.pipeline.step_embed')}</li>
                <li>{t('admin.corpus.pipeline.step_index')}</li>
                <li>{t('admin.corpus.pipeline.step_available')}</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/admin/corpus',
  component: AdminCorpusPage,
});
